import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { intervalToDuration } from 'date-fns';
import classNames from 'classnames';
import { useTranslations } from '@/app/i18n.js';
import useIsMobile from '@/app/utils/isMobile.jsx';

export const isLastChanceActive = (lastChance) => {
    if (!lastChance) {
        return false;
    }

    const target = new Date(lastChance);

    return !Number.isNaN(target.getTime()) && target > new Date();
};

const CountdownOverlay = styled.div`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    z-index: 55;
    pointer-events: none;
    width: max-content;
    max-width: calc(100% - 1.5rem);
    bottom: 7.75rem;

    @media (min-width: 768px) {
        bottom: 7.5rem;
    }

    @media (min-width: 1024px) {
        bottom: 9.25rem;
    }

    @media (min-width: 1536px) {
        bottom: 9.75rem;
    }

    &.featured {
        bottom: 7rem;

        @media (min-width: 1024px) {
            bottom: 9rem;
        }
    }

    &.xl-logo {
        bottom: 9.25rem;

        @media (min-width: 768px) {
            bottom: 9rem;
        }

        @media (min-width: 1024px) {
            bottom: 10.5rem;
        }
    }
`;

const CountdownValue = styled.span`
    display: block;
    background: #9c1919;
    color: white;
    padding: 0.2rem 0.5rem;
    border-radius: 0.2rem;
    font-family: 'Space Grotesk', monospace;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.04rem;
    line-height: 1.2;
    text-align: center;
    text-shadow: 0 2px 12px rgba(0, 0, 0, 0.65);

    @media (min-width: 768px) {
        font-size: 1rem;
        padding: 0.26rem 0.74rem;
    }
`;

const formatCountdown = (targetTime, __, isMobile = false) => {
    const now = new Date();
    const totalSeconds = Math.max(0, Math.floor((targetTime - now) / 1000));

    if (totalSeconds <= 0) {
        return null;
    }

    const difference = intervalToDuration({ start: now, end: targetTime });
    const days = difference.days ?? 0;
    const hours = difference.hours ?? 0;
    const minutes = difference.minutes ?? 0;
    const seconds = difference.seconds ?? 0;

    if (days > 0) {
        const daysLabel = __(days > 1
            ? 'book.common.last_chance_countdown.days.plural'
            : 'book.common.last_chance_countdown.days.singular');

        return `${days} ${daysLabel} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    if (hours > 0) {
        if (isMobile) {
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        }

        return __('book.common.last_chance_countdown.hours_minutes_seconds', {
            hours,
            hoursLabel: __(hours > 1
                ? 'book.common.last_chance_countdown.hours.plural'
                : 'book.common.last_chance_countdown.hours.singular'),
            minutes,
            minutesLabel: __('book.common.last_chance_countdown.minutes.short'),
            seconds,
            secondsLabel: __('book.common.last_chance_countdown.seconds.short'),
        });
    }

    if (minutes > 0) {
        return __('book.common.last_chance_countdown.minutes_and_seconds', {
            minutes,
            minutesLabel: __('book.common.last_chance_countdown.minutes.short'),
            seconds,
            secondsLabel: __('book.common.last_chance_countdown.seconds.short_plural'),
        });
    }

    const secondsLabel = __(seconds > 1
        ? 'book.common.last_chance_countdown.seconds.plural'
        : 'book.common.last_chance_countdown.seconds.singular');

    return `${seconds} ${secondsLabel}`;
};

const LastChanceCountdown = ({ targetTime, onExpire }) => {
    const __ = useTranslations();
    const { isMobile } = useIsMobile();
    const [timeLeft, setTimeLeft] = useState(() => formatCountdown(targetTime, __, isMobile));

    useEffect(() => {
        const updateCountdown = () => {
            const formatted = formatCountdown(targetTime, __, isMobile);

            if (!formatted) {
                setTimeLeft(null);
                onExpire?.();
                return false;
            }

            setTimeLeft(formatted);
            return true;
        };

        if (!updateCountdown()) {
            return undefined;
        }

        const intervalId = setInterval(() => {
            if (!updateCountdown()) {
                clearInterval(intervalId);
            }
        }, 1000);

        return () => clearInterval(intervalId);
    }, [targetTime, onExpire, __, isMobile]);

    if (!timeLeft) {
        return null;
    }

    return <CountdownValue>{timeLeft}</CountdownValue>;
};

const GameCardLastChance = ({ lastChance, variant = 'default', xlLogo = false, className = '' }) => {
    const [active, setActive] = useState(() => isLastChanceActive(lastChance));

    if (!lastChance || !active) {
        return null;
    }

    const targetTime = new Date(lastChance);

    return (
        <CountdownOverlay className={classNames(className, {
            featured: variant === 'featured',
            'xl-logo': xlLogo,
        })}>
            <LastChanceCountdown
                targetTime={targetTime}
                onExpire={() => setActive(false)}
            />
        </CountdownOverlay>
    );
};

export default GameCardLastChance;
